import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Job } from '../types';
import { getJob } from '../services/api';
import { format } from 'date-fns';
import LoadingSpinner from './LoadingSpinner';

/**
 * JobDetailView Component
 * 
 * Displays detailed information for a single job, including formatted timestamps,
 * calculated processing duration, S3 paths, and error messages when applicable.
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */

interface JobDetailViewProps {
  jobId?: string; // Optional prop for testing, otherwise uses route param
}

export const JobDetailView: React.FC<JobDetailViewProps> = ({ jobId: propJobId }) => {
  const { jobId: routeJobId } = useParams<{ jobId: string }>();
  const jobId = propJobId || routeJobId;
  const navigate = useNavigate();
  
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch job details by ID
   * Requirement 4.1: Display all job attributes
   */
  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) {
        setError('No job ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const jobData = await getJob(jobId);
        setJob(jobData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load job details');
        console.error('Error fetching job details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  /**
   * Format timestamp to local timezone
   * Requirement 4.5: Format timestamps in local timezone with format "YYYY-MM-DD HH:MM:SS"
   */
  const formatTimestamp = (timestamp?: number): string => {
    if (!timestamp) return '-';
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
  };

  /**
   * Calculate processing duration in seconds
   * Requirement 4.3: Calculate and display processing duration
   */
  const calculateDuration = (startTime?: number, endTime?: number): string => {
    if (!startTime || !endTime) return '-';
    const durationMs = endTime - startTime;
    const durationSeconds = (durationMs / 1000).toFixed(2);
    return `${durationSeconds} seconds`;
  };

  /**
   * Format S3 path
   * Requirement 4.4: Display complete S3 path as "s3://{bucket}/{key}"
   */
  const formatS3Path = (bucket?: string, key?: string): string => {
    if (!bucket || !key) return '-';
    return `s3://${bucket}/${key}`;
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Loading job details..." />
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4 m-4">
        <h3 className="text-red-800 font-semibold mb-2">Error Loading Job Details</h3>
        <p className="text-red-700">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Job List
        </button>
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (!job) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Job not found</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          <span className="mr-2">←</span> Back to Job List
        </button>
        <h2 className="text-2xl font-semibold text-gray-900">Job Details</h2>
      </div>

      {/* Job Details Card */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Status Banner - Requirement 4.2: Display error message if job has error */}
        {job.status === 'failed' && job.error && (
          <div className="bg-red-50 border-b border-red-200 p-4">
            <h3 className="text-red-800 font-semibold mb-2">Error</h3>
            <p className="text-red-700">{job.error}</p>
          </div>
        )}

        {/* Job Attributes - Requirement 4.1: Display all attributes */}
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            {/* Job ID */}
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Job ID</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{job.jobId}</dd>
            </div>

            {/* Filename */}
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Filename</dt>
              <dd className="mt-1 text-sm text-gray-900">{job.filename}</dd>
            </div>

            {/* Site */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Site</dt>
              <dd className="mt-1 text-sm text-gray-900">{job.site}</dd>
            </div>

            {/* Press */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Press</dt>
              <dd className="mt-1 text-sm text-gray-900">{job.press}</dd>
            </div>

            {/* Status */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  job.status === 'completed' ? 'bg-green-100 text-green-800' :
                  job.status === 'failed' ? 'bg-red-100 text-red-800' :
                  job.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                  job.status === 'expired' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {job.status}
                </span>
              </dd>
            </div>

            {/* Processing Duration - Requirement 4.3 */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Processing Duration</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {calculateDuration(job.startTime, job.endTime)}
              </dd>
            </div>

            {/* Start Time - Requirement 4.5 */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Start Time</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatTimestamp(job.startTime)}</dd>
            </div>

            {/* End Time - Requirement 4.5 */}
            <div>
              <dt className="text-sm font-medium text-gray-500">End Time</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatTimestamp(job.endTime)}</dd>
            </div>

            {/* S3 Path - Requirement 4.4 */}
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">S3 Path</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono break-all">
                {formatS3Path(job.s3Bucket, job.s3Key)}
              </dd>
            </div>

            {/* S3 Bucket */}
            <div>
              <dt className="text-sm font-medium text-gray-500">S3 Bucket</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{job.s3Bucket}</dd>
            </div>

            {/* S3 Key */}
            <div>
              <dt className="text-sm font-medium text-gray-500">S3 Key</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{job.s3Key}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default JobDetailView;
